import React, {memo, useState} from "react";
import {endOfDay, formatISO, parseISO, startOfDay} from "date-fns";

import {isValidDate} from "../../../utils/date";
import FiltersBox from "../../../components/FiltersBox";
import StationsAutocomplete from "../../../components/inputs/StationsAutocomplete";
import MicroZonesAutocomplete from "../../../components/inputs/MicroZonesAutocomplete";
import WorkgroupsAutocomplete from "../../../components/inputs/WorkgroupsAutocomplete";
import SeasonSelect from "../../../components/inputs/SeasonSelect";
import DatesPreselected from "../../../components/inputs/DatesPreselected";
import DateGte from "../../../components/inputs/DateGte";
import Model1StatusAutocomplete from "../../../components/inputs/Model1StatusAutocomplete";
import DateLte from "../../../components/inputs/DateLte";
import UsersAutocomplete from "../../../components/inputs/UsersAutocomplete";
import GroupAutocomplete from "../../../components/inputs/GroupAutocomplete";

import {Grid} from "@mui/material";
import Stack from "@mui/material/Stack";
import Elevation from "../../../components/inputs/Elevation";
import StationWritePermissionCheckBox from "../../../components/inputs/StationWritePermissionCheckBox";
import StationTypeSelect from "../../../components/inputs/StationTypeSelect";


const Filters = memo(({onChange, initialValues, loading}) => {
    const [selectedWorkgroups, setSelectedWorkgroups] = useState(initialValues.selectedWorkgroups ?? []);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedStations, setSelectedStations] = useState([]);
    const [selectedMicroZones, setSelectedMicroZones] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [dateGte, setDateGte] = useState(parseISO(initialValues.datetimeGte ?? null));
    const [dateLte, setDateLte] = useState(parseISO(initialValues.datetimeLte ?? null));
    const [elevationGte, setElevationGte] = useState(null);
    const [elevationLte, setElevationLte] = useState(null);
    const [stationAclModel23Write, setAclModel23Write] = useState(initialValues.stationAclModel23Write ?? false);
    const [stationType, setStationType] = useState(initialValues.stationType ?? null);

    const filters = {
        "station__id__in": selectedStations.map(s => s.id).join(','),
        "station__workgroup_id__in": selectedWorkgroups.map(w => w.id).join(','),
        "micro_zone_id__in": selectedMicroZones.map(m => m.id).join(','),
        "user__id__in": selectedUsers.map(u => u.id).join(','),
        "group__id__in": selectedGroups.map(g => g.id).join(','),
        "status__in": selectedStatuses.join(','),
        "datetime__gte": isValidDate(dateGte) ? formatISO(startOfDay(dateGte)) : null,
        "datetime__lte": isValidDate(dateLte) ? formatISO(endOfDay(dateLte)) : null,
        "elevation__gte": elevationGte,
        "elevation__lte": elevationLte,
        "station__acl__model23_write": stationAclModel23Write,
        "station__type": stationType,
    }

    const handleOnApplyFilters = () => {
        onChange(filters);
    }

    return <FiltersBox onApplyFilters={handleOnApplyFilters} isLoading={loading}>
        <Grid container spacing={2} justifyContent="space-between">

            <Grid item xs={12} md={3}>
                <Stack direction="column" spacing={1}>

                    <StationsAutocomplete selectedStations={selectedStations}
                                          setSelectedStations={setSelectedStations}
                                          fullWidth
                                          stationFilters={{workgroup_id__in: filters.station__workgroup_id__in, micro_zone_id__in: filters.micro_zone_id__in, accept_mod23: true,}}/>
                    <MicroZonesAutocomplete selectedMicroZones={selectedMicroZones}
                                            setSelectedMicroZones={setSelectedMicroZones}
                                            microZoneFilter={{workgroup_id__in: filters.station__workgroup_id__in}}
                                            fullWidth/>
                    <WorkgroupsAutocomplete selectedWorkgroups={selectedWorkgroups}
                                            setSelectedWorkgroups={setSelectedWorkgroups}
                                            fullWidth/>
                    <StationWritePermissionCheckBox value={stationAclModel23Write} onChange={setAclModel23Write}/>
                </Stack>
            </Grid>

            <Grid item xs={12} md={6} xl={4}>
                <Stack direction="column" spacing={1} useFlexGap>
                    <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                        <Grid item xs={12} lg={4}>
                            <SeasonSelect dateGte={dateGte} dateLte={dateLte} setDateGte={setDateGte}
                                          setDateLte={setDateLte} sx={{minWidth: "7rem"}}/>
                        </Grid>
                        <Grid item xs={12} lg={8}>
                            <DatesPreselected dateGte={dateGte} setDateGte={setDateGte} dateLte={dateLte}
                                              setDateLte={setDateLte}/>
                        </Grid>
                    </Grid>
                    <Stack direction="row" spacing={2}>
                        <DateGte dateGte={dateGte} setDateGte={setDateGte} maxDate={dateLte}/>
                        <DateLte dateLte={dateLte} setDateLte={setDateLte} mindate={dateGte}/>
                    </Stack>
                </Stack>
                <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                    <Grid item xs={12} lg={6}>
                        <Elevation elevation={elevationGte} setElevation={setElevationGte} labelType="gte"/>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Elevation elevation={elevationLte} setElevation={setElevationLte} labelType="lte"/>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <StationTypeSelect value={stationType} onChange={(e) => setStationType(e.target.value)} fullWidth/>
                    </Grid>
                </Grid>
            </Grid>

            <Grid item xs={12} md={3}>
                <UsersAutocomplete selectedUsers={selectedUsers}
                                   setSelectedUsers={setSelectedUsers}
                                   fullWidth/>
                <GroupAutocomplete selectedGroups={selectedGroups}
                                   setSelectedGroups={setSelectedGroups}
                                   fullWidth/>
                <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                    <Grid item xs={12} lg={6}>
                        <Model1StatusAutocomplete setModel1Statuses={setSelectedStatuses}
                                                  fullWidth/>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    </FiltersBox>
})

export default Filters;